import {
  AfterViewInit,
  Directive,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  Output,
} from '@angular/core';

/**
 * Local replacement for ngx-google-places-autocomplete's GooglePlaceDirective.
 *
 * That package's latest release (2.0.5) is ViewEngine-compiled, so Angular 16 -- which
 * removes ngcc -- cannot consume it at any version. The Google Maps JS API it wraps is
 * already loaded by `loadGoogleMapsFactory` in AppModule's APP_INITIALIZER, with
 * `libraries=places`, so `google.maps.places.Autocomplete` is already available.
 *
 * This is a transplant of the original 60 lines, not a reimplementation. Two behaviours
 * in particular are carried over deliberately, because both fail silently if dropped:
 *
 *  1. The keydown handler swallows Enter on the input. Without it, choosing a suggestion
 *     with the keyboard submits the surrounding form -- order creation and the delivery
 *     dialog both sit inside forms.
 *  2. The iOS workaround stops touchend propagation on `.pac-container`. Without it,
 *     tapping a suggestion on iPhone/iPad can dismiss the dropdown before the selection
 *     registers. This one cannot be verified on a desktop browser.
 *
 * The original's `exportAs: 'ngx-places'` and public `reset()` are not reproduced: no
 * template binds the directive instance (the `#placesRef` refs resolve to the input
 * element, not to this directive) and `reset()` was never called.
 *
 * The original never removed its keydown handler or the Maps listener on destroy. That
 * is carried over as-is rather than fixed, for the same reason MT-953 kept
 * ngx-custom-validators' missing unsubscribe: changing behaviour during an upgrade
 * destroys the regression baseline. Tidy it up in its own change if it matters.
 */

/** Shape of `google.maps.places.AutocompleteOptions`, as the old package declared it. */
export interface GooglePlacesOptions {
  bounds?: unknown;
  componentRestrictions?: { country: string | string[] };
  types?: string[];
  fields?: string[];
  strictBounds?: boolean;
  origin?: unknown;
}

/** Shape of `google.maps.places.PlaceResult`, as the old package's `Address` declared it. */
export interface GooglePlacesAddress {
  address_components: GooglePlacesAddressComponent[];
  adr_address: string;
  formatted_address: string;
  formatted_phone_number: string;
  geometry: any;
  html_attributions: string[];
  icon: string;
  id: string;
  international_phone_number: string;
  name: string;
  opening_hours: any;
  permanently_closed: boolean;
  photos: any[];
  place_id: string;
  [key: string]: any;
}

export interface GooglePlacesAddressComponent {
  long_name: string;
  short_name: string;
  types: string[];
}

@Directive({
  selector: '[ngx-google-places-autocomplete]',
  standalone: true,
})
export class GooglePlacesAutocompleteDirective implements AfterViewInit {
  @Input() options?: GooglePlacesOptions;
  @Output() onAddressChange = new EventEmitter<GooglePlacesAddress>();

  private autocomplete: any;

  constructor(private el: ElementRef<HTMLInputElement>, private ngZone: NgZone) {}

  ngAfterViewInit(): void {
    const google = (window as any).google;
    if (!google || !google.maps || !google.maps.places) {
      throw new Error('Google maps library can not be found');
    }

    this.autocomplete = new google.maps.places.Autocomplete(
      this.el.nativeElement,
      this.options || {}
    );
    if (!this.autocomplete) {
      throw new Error('Autocomplete is not initialized');
    }

    this.autocomplete.addListener('place_changed', () => this.handleChangeEvent());

    // Keep Enter from reaching the surrounding form while a suggestion is being picked.
    this.el.nativeElement.addEventListener('keydown', (event: KeyboardEvent) => {
      if (!event.key) {
        return;
      }
      if (event.key.toLowerCase() === 'enter' && event.target === this.el.nativeElement) {
        event.preventDefault();
        event.stopPropagation();
      }
    });

    // https://gist.github.com/schoenobates/ef578a02ac8ab6726487 -- on iOS the dropdown
    // is dismissed by the touchend before the selection lands.
    if (typeof navigator !== 'undefined' && navigator.userAgent && navigator.userAgent.match(/(iPad|iPhone|iPod)/g)) {
      setTimeout(() => {
        const containers = Array.from(document.getElementsByClassName('pac-container'));
        for (const container of containers) {
          if (!container) {
            continue;
          }
          container.addEventListener('touchend', (e: Event) => e.stopImmediatePropagation());
        }
      }, 500);
    }
  }

  private handleChangeEvent(): void {
    this.ngZone.run(() => {
      const place = this.autocomplete.getPlace();
      if (place) {
        this.onAddressChange.emit(place);
      }
    });
  }
}
